import { useState } from 'react'

import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Icon, Tooltip, typography } from '@joor/design-system'
import '@joor/design-system/dist/designTokens/variables.css'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import { fromGlobalId } from 'utils/transformations/graphql'

import { useRenderToasters } from 'hooks/useRenderToasters'
import { getBrandId, getPanelId } from 'store/storefront/selectors'

import { PageItemProps } from './Pages.types'
import { getStorefrontPanelUrl } from './Pages.utils'
import PageActions from './actions'

const STATUS_ID = 'storefront_page_status'
export const HOME_ID = 'storefront_page_home'
export const SHARE_ID = 'storefront_page_share'
export const ELLIPSES_ID = 'storefront_page_context_menu'

const DragContainer = styled.div`
  width: 100%;
`
const NoHover = styled.div``
const OnHover = styled.div`
  opacity: 0;
  display: flex;
  align-items: center;
  position: relative;
  width: 0;
  div {
    white-space: nowrap;
  }
`

const ItemContainer = styled.div`
  display: flex;
  justify-content: space-between;
  cursor: pointer;
  height: 32px;
  padding-right: 8px;
  ${typography.alpha.primaryActive.body1};
  background-color: var(--color-neutral-000-full);

  ${NoHover}:first-child {
    display: inline-block;
    width: 24px;
  }

  > div {
    display: flex;
    align-items: center;
  }

  &.active {
    background-color: var(--color-secondary-300);
  }
  &:hover {
    background-color: var(--color-secondary-100);
    ${NoHover} {
      display: none;
    }
    ${OnHover} {
      opacity: 1;
      width: auto;
    }
  }
`

const ItemName = styled.span`
  margin-left: 6px;
`

const StyledIcon = styled(Icon)`
  transform: scale(0.7);
  cursor: pointer;
`

const NoHoverIcon = styled(Icon)`
  &.online svg {
    transform: scale(0.4);
    --main-color: var(--color-success-500);
  }

  &.offline svg {
    transform: scale(0.4);
    --main-color: var(--color-error-500);
  }

  &:hover svg {
    --main-color: var(--color-primary-800);
  }
`
const PageItemContainer = styled.div`
  position: relative;
`

const PageItem = ({
  id,
  inMenu = false,
  displayName,
  uniqueName,
  isHome = false,
  isOnline = false,
  orderNbr,
  onClick,
  loadingNavigation,
  refetchStorefrontNavigation,
}: PageItemProps) => {
  const [openActions, setOpenActions] = useState<boolean>(false)
  const panelId = useSelector(getPanelId)
  const brandId = useSelector(getBrandId)
  const fromGlobalBrandId = fromGlobalId(brandId)?.id
  const { renderSuccessToast, renderErrorToast } = useRenderToasters()
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id, data: { inMenu } })

  const handleCopyUrlClick = async () => {
    try {
      const url = getStorefrontPanelUrl(isHome, uniqueName, fromGlobalBrandId)
      await navigator.clipboard.writeText(url)
      renderSuccessToast({ description: 'Page URL copied.' })
    } catch {
      renderErrorToast({ description: 'There was an error copying the URL.' })
    }
  }
  return (
    <PageItemContainer {...attributes}>
      <ItemContainer
        ref={setNodeRef}
        style={{
          transform: CSS.Transform.toString(transform),
          transition,
          cursor: isDragging ? 'grabbing' : 'grab',
          ...(isDragging && {
            zIndex: 20001,
            background: 'var(--color-secondary-300)',
          }),
        }}
        className={panelId === id ? 'active' : ''}
      >
        <DragContainer onClick={onClick} {...listeners}>
          <NoHover />
          <OnHover>
            <NoHoverIcon iconName="kabob" />
          </OnHover>
          <NoHoverIcon
            className={isOnline ? 'online' : 'offline'}
            iconName="circle"
            data-testid={STATUS_ID}
          />
          <ItemName>{displayName}</ItemName>
        </DragContainer>
        <div>
          {isHome && (
            <StyledIcon
              iconName="home"
              // @ts-expect-error
              variant="SUBDUED"
              data-testid={HOME_ID}
            />
          )}
          {isOnline && (
            <OnHover onClick={handleCopyUrlClick}>
              <Tooltip defaultPlacement="bottom" content="Copy Page URL">
                <StyledIcon iconName="share" data-testid={SHARE_ID} />
              </Tooltip>
            </OnHover>
          )}
          <div
            onClick={(e) => {
              e.stopPropagation()
              setOpenActions(!openActions)
            }}
          >
            <OnHover>
              {openActions ? (
                <StyledIcon
                  iconName="ellipses"
                  data-testid={`${ELLIPSES_ID}_${id}_${isHome}`}
                />
              ) : (
                <Tooltip defaultPlacement="bottom" content={'Page actions'}>
                  <StyledIcon
                    iconName="ellipses"
                    data-testid={`${ELLIPSES_ID}_${id}_${isHome}`}
                  />
                </Tooltip>
              )}
            </OnHover>
          </div>
        </div>
      </ItemContainer>
      <PageActions
        isHome={isHome}
        isOnline={isOnline}
        id={id}
        orderNbr={orderNbr}
        openActions={openActions}
        setOpenActions={setOpenActions}
        displayName={displayName}
        loadingNavigation={loadingNavigation}
        refetchStorefrontNavigation={refetchStorefrontNavigation}
      />
    </PageItemContainer>
  )
}

export default PageItem
